import { useCallback, useEffect, useMemo } from 'react'
import { useVdTracking } from '../../../../ng-compat/hooks'

export const useTrackTransactionDuration = ({
  registerId,
  transactionId,
  amount,
  paymentTypeId,
  isRefund,
}: {
  registerId: string
  transactionId: string
  amount: number
  paymentTypeId: number
  isRefund: boolean
}): ((completed: boolean) => void) => {
  const vdTracking = useVdTracking()
  // ensure start time is not recalculated between renders by using useMemo with no dependencies.
  const startTime = useMemo(() => Date.now(), [])
  const transactionType = isRefund ? 'refund' : 'payment'

  const metadata = useMemo(() => {
    return {
      registerId,
      transactionId,
      amount,
      paymentTypeId,
      transactionType,
      startTime: new Date(startTime).toUTCString(),
    }
  }, [
    registerId,
    transactionId,
    amount,
    paymentTypeId,
    transactionType,
    startTime,
  ])

  // Send transaction start ube on first render
  useEffect(() => {
    vdTracking.trackUserEvent({
      eventName: `Transaction for payment type ${metadata.paymentTypeId} started.`,
      metadata,
    })
  }, [vdTracking, metadata])

  const trackEnd = useCallback(
    (completed: boolean) => {
      vdTracking.trackUserEvent({
        eventName: `Transaction for payment type ${metadata.paymentTypeId} ended.`,
        metadata: {
          ...metadata,
          endTime: new Date().toUTCString(),
          // duration in seconds when ending the flow
          duration: (Date.now() - startTime) / 1000,
          completed,
        },
      })
    },
    [vdTracking, metadata, startTime]
  )

  return trackEnd
}
